import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcChartLine, IcPdf, IcExcel } from "../../Icons";
import Header from "../../layout/Header/Header";
import { Button, Input, Label, ButtonGroup, Table, Spinner } from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { pdfHeader, pdfFooter } from "../../../pdfUtils";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

// ── Configuración ─────────────────────────────────────────────────────────────
const ANIO_INICIO    = 2021;
const BOLD_LABELS    = ["Venta", "Gastos", "Utilidad", "Cobranza", "Retiro", "Retiro Mensual"];
const ROW_ORDER      = ["Venta", "Gastos", "Gastos %", "Utilidad", "Utilidad %", "Cobranza", "Retiro"];
const BG_GRAY        = "#f2f2f2";
const DELTA_ROWS     = new Set(["Venta", "Gastos", "Utilidad", "Cobranza", "Retiro", "Retiro Mensual"]);
const INVERTED_ROWS  = new Set(["Gastos"]); // subir es malo → colores invertidos

// ── Formateo ──────────────────────────────────────────────────────────────────
const fmtMXN = (v) =>
  new Intl.NumberFormat("es-MX", {
    style:                 "currency",
    currency:              "MXN",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v) || 0);

const fmtPct = (v) => `${((Number(v) || 0) * 100).toFixed(1)}%`;

// ── Delta año vs año anterior ──────────────────────────────────────────────────
function calcDelta(curr, prev) {
  if (prev === 0 || prev == null) return null;
  return { pct: ((curr - prev) / Math.abs(prev)) * 100, amt: curr - prev };
}

// ── Componente ────────────────────────────────────────────────────────────────
function BalanceAnual() {
  const { user }      = useContext(AuthContext);
  const URL_GRAFICAS  = process.env.REACT_APP_URL_GRAFICAS;

  const anioActual = new Date().getFullYear();
  const mesActual  = new Date().getMonth() + 1; // 1-12
  const todosAnios = Array.from(
    { length: anioActual - ANIO_INICIO + 1 },
    (_, i) => ANIO_INICIO + i
  );

  const [anioDesde,  setAnioDesde]  = useState("2025");
  const [anioHasta,  setAnioHasta]  = useState(anioActual);
  const [loading,    setLoading]    = useState(false);
  const [annualData, setAnnualData] = useState(null); // { years: [], rows: [] }

  // ── Fetch ───────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const years   = [];
      for (let y = Number(anioDesde); y <= Number(anioHasta); y++) years.push(y);

      const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };

      const responses = await Promise.all(
        years.map((y) =>
          axios.get(
            `${URL_GRAFICAS}/balanceMensual/${y}-01-01/${y}-12-31`,
            { headers }
          )
        )
      );

      // ── Totales por etiqueta y año ────────────────────────────────────────
      const totalesPorLabel = {};
      ROW_ORDER.forEach((lbl) => { totalesPorLabel[lbl] = []; });

      responses.forEach((res) => {
        const apiRows = res.data?.rows || [];
        ROW_ORDER.forEach((lbl) => {
          const found = apiRows.find((r) => r.label === lbl);
          totalesPorLabel[lbl].push(Number(found?.total) || 0);
        });
      });

      // ── Resumen global (para columnas Total y Promedio) ───────────────────
      const sumVenta    = totalesPorLabel["Venta"].reduce((a, b) => a + b, 0);
      const sumGastos   = totalesPorLabel["Gastos"].reduce((a, b) => a + b, 0);
      const sumUtilidad = totalesPorLabel["Utilidad"].reduce((a, b) => a + b, 0);
      const n           = years.length || 1;

      const buildRow = (lbl) => {
        const values  = totalesPorLabel[lbl];
        const isPct   = lbl.includes("%");
        let total, promedio;

        if (lbl === "Gastos %") {
          total    = sumVenta > 0 ? sumGastos   / sumVenta : 0;
          promedio = total;
        } else if (lbl === "Utilidad %") {
          total    = sumVenta > 0 ? sumUtilidad / sumVenta : 0;
          promedio = total;
        } else {
          total    = values.reduce((a, b) => a + b, 0);
          promedio = total / n;
        }

        return { label: lbl, values, total, promedio, isPct, subRow: false };
      };

      const baseRows = ROW_ORDER.map(buildRow);

      // ── Retiro Mensual (retiro ÷ meses transcurridos por año) ─────────────
      const retiroValues = totalesPorLabel["Retiro"];
      const retiroMensualValues = years.map((y, i) => {
        const meses = y === anioActual ? mesActual : 12;
        return retiroValues[i] / meses;
      });
      const totalMesesGlobal = years.reduce(
        (sum, y) => sum + (y === anioActual ? mesActual : 12),
        0
      );
      const sumRetiro = retiroValues.reduce((a, b) => a + b, 0);
      const retiroMensualRow = {
        label:    "Retiro Mensual",
        values:   retiroMensualValues,
        total:    totalMesesGlobal > 0 ? sumRetiro / totalMesesGlobal : 0,
        promedio: retiroMensualValues.reduce((a, b) => a + b, 0) / n,
        isPct:    false,
        subRow:   true,
      };

      setAnnualData({ years, rows: [...baseRows, retiroMensualRow] });
    } catch (e) {
      console.error("BalanceAnual fetch error:", e);
    }
    setLoading(false);
  }, [anioDesde, anioHasta, URL_GRAFICAS, anioActual, mesActual]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── PDF ─────────────────────────────────────────────────────────────────────
  function exportPDF() {
    if (!annualData) return;
    const { years, rows } = annualData;

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const startY = pdfHeader(
      doc,
      "Balance Anual",
      `${years[0]} – ${years[years.length - 1]}`
    );

    const head = [["Concepto", ...years.map(String), "Total", "Prom."]];

    const body = rows.map((r) => [
      r.label,
      ...r.values.map((v) => (r.isPct ? fmtPct(v) : fmtMXN(v))),
      r.isPct ? fmtPct(r.total)    : fmtMXN(r.total),
      r.isPct ? fmtPct(r.promedio) : fmtMXN(r.promedio),
    ]);

    // ── Delta map (rowIdx → colIdx → {delta, label}) ──────────────────────────
    const deltaMap = {};
    rows.forEach((r, rowIdx) => {
      if (!DELTA_ROWS.has(r.label)) return;
      r.values.forEach((v, i) => {
        if (i === 0) return;
        const thisYear = years[i];
        const rawPrev  = r.values[i - 1];
        const adjPrev  = (thisYear === anioActual && mesActual < 12 && r.label !== "Retiro Mensual")
          ? rawPrev * (mesActual / 12)
          : rawPrev;
        const delta = calcDelta(v, adjPrev);
        if (delta) deltaMap[`${rowIdx}_${i + 1}`] = { delta, label: r.label };
      });
    });

    // Anchos dinámicos
    const pageW     = doc.internal.pageSize.getWidth();
    const mL = 8, mR = 8;
    const available = pageW - mL - mR;
    const conceptoW = 30;
    const summaryW  = 22;
    const yearW     = (available - conceptoW - summaryW * 2) / Math.max(years.length, 1);

    const totalIdx    = years.length + 1;
    const promedioIdx = years.length + 2;

    const colStyles = { 0: { cellWidth: conceptoW, halign: "left" } };
    years.forEach((_, i) => { colStyles[i + 1] = { cellWidth: yearW,    halign: "center" }; });
    colStyles[totalIdx]    = { cellWidth: summaryW, halign: "center" };
    colStyles[promedioIdx] = { cellWidth: summaryW, halign: "center" };

    doc.autoTable({
      startY,
      head,
      body,
      columnStyles: colStyles,
      styles: {
        fontSize:       9,
        cellPadding:    { top: 2.5, right: 3, bottom: 2.5, left: 3 },
        overflow:       "hidden",
        font:           "helvetica",
        lineColor:      [226, 232, 240],
        lineWidth:      0.25,
        minCellHeight:  13, // espacio para monto + delta
      },
      headStyles: {
        fillColor:   [15, 23, 42],
        textColor:   [255, 255, 255],
        fontStyle:   "bold",
        fontSize:    8.5,
        halign:      "center",
        cellPadding: { top: 3.5, right: 3, bottom: 3.5, left: 3 },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      didParseCell: (data) => {
        const lbl      = data.row.raw?.[0];
        const colIndex = data.column.index;
        const isHead   = data.row.section === "head";

        if (!isHead && BOLD_LABELS.includes(lbl)) {
          data.cell.styles.fontStyle = "bold";
        }
        if (colIndex === totalIdx) {
          data.cell.styles.fillColor = [242, 242, 242];
          data.cell.styles.textColor = [15, 23, 42];
        }
        if (colIndex === promedioIdx && !isHead) {
          data.cell.styles.fillColor = [242, 242, 242];
          data.cell.styles.textColor = [15, 23, 42];
        }
      },
      didDrawCell: (data) => {
        if (data.row.section !== "body") return;
        const entry = deltaMap[`${data.row.index}_${data.column.index}`];
        if (!entry) return;

        const { delta, label } = entry;
        const isGood  = INVERTED_ROWS.has(label) ? delta.pct < 0 : delta.pct > 0;
        const color   = isGood ? [22, 163, 74] : [220, 38, 38];
        const sign    = delta.pct >= 0 ? "+" : "-";
        const pctTxt  = `${sign}${Math.abs(delta.pct).toFixed(1)}%`;
        const amtTxt  = `(${delta.amt >= 0 ? "+" : ""}${fmtMXN(delta.amt)})`;

        doc.setFontSize(6.5);
        doc.setTextColor(...color);
        doc.text(
          `${pctTxt}  ${amtTxt}`,
          data.cell.x + data.cell.width / 2,
          data.cell.y + data.cell.height - 1.8,
          { align: "center" }
        );
        // Resetear estilos
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(9);
      },
      margin:     { left: mL, right: mR },
      tableWidth: available,
    });

    pdfFooter(doc);
    doc.save(`Balance_Anual_${years[0]}-${years[years.length - 1]}.pdf`);
  }

  // ── Excel ───────────────────────────────────────────────────────────────────
  async function exportExcel() {
    if (!annualData?.rows?.length) return;
    const { years, rows } = annualData;

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("BalanceAnual");

    const grayFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
    const moneyFmt = '"$"#,##0;[Red]"$"#,##0';
    const pctFmt   = "0.0%";

    const nYears   = years.length;
    const totalCol = nYears + 2; // 1-based (A=1)
    const avgCol   = nYears + 3;

    // Cabecera
    ws.addRow(["CONCEPTO", ...years.map(String), "TOTAL", "PROMEDIO"]);
    ws.getRow(1).font      = { bold: true, size: 11 };
    ws.getRow(1).alignment = { vertical: "middle", horizontal: "center" };
    ws.getRow(1).height    = 18;
    ws.getCell(1, totalCol).fill = grayFill;
    ws.getCell(1, avgCol).fill   = grayFill;

    // Anchos
    ws.getColumn(1).width = 16;
    for (let c = 2; c <= avgCol; c++) ws.getColumn(c).width = 16;

    // Filas de datos
    rows.forEach((r) => {
      const rowData = [r.label, ...r.values, r.total, r.promedio];
      const exRow   = ws.addRow(rowData);
      const rNum    = exRow.number;

      exRow.alignment = { vertical: "middle", horizontal: "center" };
      ws.getCell(rNum, 1).alignment = { vertical: "middle", horizontal: "left" };

      if (BOLD_LABELS.includes(r.label)) {
        exRow.font = { bold: true };
      }

      for (let c = 2; c <= avgCol; c++) {
        const cell = ws.getCell(rNum, c);
        cell.numFmt = r.isPct ? pctFmt : moneyFmt;
        if (c >= totalCol) cell.fill = grayFill;
        cell.border = {
          top:    { style: "thin", color: { argb: "FFD0D0D0" } },
          left:   { style: "thin", color: { argb: "FFD0D0D0" } },
          bottom: { style: "thin", color: { argb: "FFD0D0D0" } },
          right:  { style: "thin", color: { argb: "FFD0D0D0" } },
        };
      }
      ws.getCell(rNum, 1).border = {
        top:    { style: "thin", color: { argb: "FFD0D0D0" } },
        left:   { style: "thin", color: { argb: "FFD0D0D0" } },
        bottom: { style: "thin", color: { argb: "FFD0D0D0" } },
        right:  { style: "thin", color: { argb: "FFD0D0D0" } },
      };
    });

    ws.views = [{ state: "frozen", xSplit: 1, ySplit: 1 }];

    const buf = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `Balance_Anual_${years[0]}-${years[years.length - 1]}.xlsx`
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />
      {user.menu_usuarios ? (
        <div className="card container col-sm-12">

          {/* ── Page header ──────────────────────────────────────────────── */}
          <div className="page-header">
            <div>
              <h3 className="page-title">
                <IcChartLine style={{ marginRight: 8, color: "#8b5cf6" }} />
                Balance Anual
              </h3>
              <p className="page-subtitle">Comparativo de venta, gastos y utilidad por año</p>
            </div>
            <div className="page-actions">
              <ButtonGroup>
                <Button size="sm" color="danger" onClick={exportPDF} disabled={!annualData || loading}>
                  <IcPdf style={{ marginRight: 4 }} /> PDF
                </Button>
                <Button size="sm" color="primary" onClick={exportExcel} disabled={!annualData || loading}>
                  <IcExcel style={{ marginRight: 4 }} /> Excel
                </Button>
              </ButtonGroup>
            </div>
          </div>

          {/* ── Filtros ──────────────────────────────────────────────────── */}
          <div className="filter-bar">
            <div>
              <Label>Desde</Label>
              <Input
                bsSize="sm"
                type="select"
                style={{ minWidth: 100 }}
                value={anioDesde}
                onChange={(e) => setAnioDesde(Number(e.target.value))}
              >
                {todosAnios.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Input>
            </div>
            <div>
              <Label>Hasta</Label>
              <Input
                bsSize="sm"
                type="select"
                style={{ minWidth: 100 }}
                value={anioHasta}
                onChange={(e) => setAnioHasta(Number(e.target.value))}
              >
                {todosAnios
                  .filter((y) => y >= anioDesde)
                  .map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
              </Input>
            </div>
          </div>


          {/* Tabla */}
          {loading ? (
            <div className="text-center py-4">
              <Spinner style={{ width: "2rem", height: "2rem", color: "#3b82f6" }} />
              <p style={{ marginTop: 10, color: "#64748b", fontSize: "0.82rem" }}>
                Cargando {anioHasta - anioDesde + 1} años…
              </p>
            </div>
          ) : annualData ? (
            <Table bordered size="sm" className="balance-table table-dark-header">
              <thead>
                <tr>
                  <th>Concepto</th>
                  {annualData.years.map((y) => (
                    <th key={y} style={{ textAlign: "center" }}>{y}</th>
                  ))}
                  <th style={{ textAlign: "center" }}>Total</th>
                  <th style={{ textAlign: "center" }}>Promedio</th>
                </tr>
              </thead>
              <tbody>
                {annualData.rows.map((r) => {
                  const isBold    = BOLD_LABELS.includes(r.label);
                  const showDelta = DELTA_ROWS.has(r.label);
                  const cellStyle = {
                    textAlign:  "center",
                    fontWeight: isBold ? "bold" : "normal",
                    verticalAlign: "middle",
                  };
                  return (
                    <tr key={r.label}>
                      <td style={{ fontWeight: isBold ? "bold" : "normal", verticalAlign: "middle" }}>
                        {r.label}
                      </td>

                      {r.values.map((v, i) => {
                        const thisYear = annualData.years[i];
                        const delta = (showDelta && i > 0)
                          ? (() => {
                              const rawPrev = r.values[i - 1];
                              // Si es el año en curso, prorratear el anterior a los mismos meses
                              // Excepción: Retiro Mensual ya es promedio ÷ meses, comparar directo
                              const adjPrev = (thisYear === anioActual && mesActual < 12 && r.label !== "Retiro Mensual")
                                ? rawPrev * (mesActual / 12)
                                : rawPrev;
                              return calcDelta(v, adjPrev);
                            })()
                          : null;
                        let deltaColor = "#6b7280";
                        let arrowChar  = "";
                        if (delta) {
                          const isGood = INVERTED_ROWS.has(r.label) ? delta.pct < 0 : delta.pct > 0;
                          deltaColor   = delta.pct === 0 ? "#6b7280" : isGood ? "#16a34a" : "#dc2626";
                          arrowChar    = delta.pct > 0 ? "▲" : delta.pct < 0 ? "▼" : "—";
                        }
                        return (
                          <td key={i} style={cellStyle}>
                            {r.isPct ? fmtPct(v) : fmtMXN(v)}
                            {delta && (
                              <div style={{
                                fontSize:   "0.78rem",
                                color:      deltaColor,
                                marginTop:  3,
                                lineHeight: 1.3,
                                fontWeight: "normal",
                              }}>
                                <span style={{ fontWeight: "bold" }}>
                                  {arrowChar} {Math.abs(delta.pct).toFixed(1)}%
                                </span>
                                <span style={{ opacity: 0.70, marginLeft: 4 }}>
                                  ({delta.amt >= 0 ? "+" : ""}{fmtMXN(delta.amt)})
                                </span>
                              </div>
                            )}
                          </td>
                        );
                      })}

                      <td style={{ ...cellStyle, backgroundColor: BG_GRAY }}>
                        {r.isPct ? fmtPct(r.total) : fmtMXN(r.total)}
                      </td>
                      <td style={{ ...cellStyle, backgroundColor: BG_GRAY }}>
                        {r.isPct ? fmtPct(r.promedio) : fmtMXN(r.promedio)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : null}

        </div>
      ) : undefined}
    </>
  );
}

export default BalanceAnual;
